import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { Box, Card, Chip, IconButton, List, ListItemButton, Skeleton, Stack, Typography, useTheme } from '@mui/material'
import { ChevronLeft, ChevronRight } from '@mui/icons-material'
import { alpha } from '@mui/material/styles'
import { useSmartTranslation } from '../../../../../generic/hooks/useSmartTranslation'
import { useDateFormatter } from '../../../../../generic/hooks/useDateFormatter'
import { systemValueLabel } from '../../../../../generic/systemValues/systemValueLabel'

const getMarkerColor = (item, theme) => {
  const isClosed = item?.endDate || item?.closeDate
  if (!isClosed) return alpha(theme.palette.error.main, 0.85)

  const statusName = String(item?.incidence?.status?.name ?? '').toUpperCase() // system-value: raw-ok
  if (statusName.includes('STOP')) return alpha(theme.palette.error.main, 0.85)
  if (statusName.includes('PENDING')) return alpha(theme.palette.warning.main, 0.85)
  if (statusName.includes('WAIT')) return alpha(theme.palette.warning.main, 0.85)
  if (statusName.includes('ERROR')) return alpha(theme.palette.error.main, 0.85)

  return alpha(theme.palette.success.main, 0.85)
}

const IncidenceHistoryColumn = ({ incidences, loading, error, onSelect, selectedIncidenceId, compact, expanded, onToggleExpanded, fullWidth, headerAction, elevation = 3, cardSx }) => {
  const theme = useTheme()
  const { t_ } = useSmartTranslation()
  const { formatTime } = useDateFormatter()
  const showMeta = Boolean(expanded)
  const width = fullWidth ? '100%' : (compact ? 160 : 264)
  const itemMinHeight = compact ? (showMeta ? 72 : 64) : 68

  const rows = useMemo(() => {
    return (incidences || []).map((item, idx) => {
      const statusLabel = systemValueLabel(t_, 'status', item?.incidence?.status) || String(item?.status || '').trim() || '-'
      const title = String(item?.incidence?.name || item?.incidence?.code || '').trim() || t_('Incidence')
      const createdBy = String(item?.createdBy?.code || '').trim()
      const startTime = item?.startDate ? formatTime(item.startDate) : ''
      const endTime = item?.endDate ? formatTime(item.endDate) : ''
      const isOpen = !item?.endDate && !item?.closeDate
      const duration = String(item?.duration || '').trim()
      const color = getMarkerColor(item, theme)
      const hasComment = Boolean(String(item?.comment || '').trim())

      return {
        key: item?.id ?? `${idx}-${item?.startDate ?? ''}`,
        raw: item,
        title,
        statusLabel,
        createdBy,
        startTime,
        endTime,
        isOpen,
        duration,
        color,
        hasComment
      }
    })
  }, [formatTime, incidences, t_, theme])

  const handleHeaderActionClick = headerAction?.onClick
  const handleToggleExpandedClick = onToggleExpanded

  const headerActionNode = headerAction?.onClick
    ? (
      <IconButton
        onClick={handleHeaderActionClick}
        aria-label={headerAction.ariaLabel || t_('Action')}
        size='small'
        sx={{
          width: 40,
          height: 40,
          borderRadius: 2,
          bgcolor: alpha(theme.palette.primary.main, 0.08),
          border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
          '&:hover': {
            bgcolor: alpha(theme.palette.primary.main, 0.12)
          }
        }}
      >
        {headerAction.icon}
      </IconButton>
      )
    : (compact && onToggleExpanded
        ? (
          <IconButton
            onClick={handleToggleExpandedClick}
            aria-label={expanded ? t_('Collapse history') : t_('Expand history')}
            size='small'
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.12)
              }
            }}
          >
            {expanded ? <ChevronRight fontSize='small' /> : <ChevronLeft fontSize='small' />}
          </IconButton>
          )
        : null)

  return (
    <Card
      elevation={elevation}
      sx={{
        width,
        height: '100%',
        borderRadius: 3,
        bgcolor: alpha(theme.palette.background.paper, 0.92),
        border: `1px solid ${alpha(theme.palette.primary.main, 0.22)}`,
        overflow: 'hidden',
        transition: fullWidth ? undefined : 'width 160ms ease',
        ...cardSx
      }}
      aria-label={t_('Incidence history')}
    >
      <Stack sx={{ height: '100%' }}>
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          sx={{
            px: 1.5,
            py: 1,
            bgcolor: alpha(theme.palette.primary.main, 0.05),
            borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.14)}`
          }}
        >
          <Stack spacing={0.2} sx={{ minWidth: 0 }}>
            <Typography variant='overline' sx={{ color: 'text.secondary', lineHeight: 1.1 }}>
              {t_('HISTORY')}
            </Typography>
            <Stack direction='row' spacing={0.75} alignItems='baseline'>
              <Typography variant='body2' sx={{ fontWeight: 800, lineHeight: 1.15 }}>
                {incidences?.length ?? 0}
              </Typography>
            </Stack>
          </Stack>
          {headerActionNode}
        </Stack>

        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            px: 1.25,
            py: 1,
            '&::-webkit-scrollbar': { width: 10 },
            '&::-webkit-scrollbar-track': { background: 'transparent' },
            '&::-webkit-scrollbar-thumb': {
              background: alpha(theme.palette.common.white, 0.22),
              borderRadius: 999,
              border: `3px solid ${alpha(theme.palette.background.paper, 0.4)}`
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: alpha(theme.palette.common.white, 0.32)
            }
          }}
        >
          {loading && rows.length === 0
            ? (
              <Stack spacing={1}>
                {Array.from({ length: 8 }).map((_, i) => (
                  <Stack key={i} spacing={0.6}>
                    <Skeleton variant='rounded' height={22} sx={{ borderRadius: 1.25 }} />
                    <Skeleton variant='rounded' height={12} sx={{ borderRadius: 1.25, width: '80%' }} />
                  </Stack>
                ))}
              </Stack>
              )
            : error && rows.length === 0
              ? (
                <Typography variant='caption' color='error'>
                  {t_('An error occurred')}
                </Typography>
                )
              : rows.length === 0
                ? (
                  <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                    {t_('No data')}
                  </Typography>
                  )
                : (
                  <List disablePadding sx={{ m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    {rows.map((row) => {
                      const timeRange = row.endTime ? `${row.startTime} - ${row.endTime}` : row.startTime
                      const meta = [row.duration, timeRange, row.createdBy].filter(Boolean).join(' · ')
                      const isSelected = Number.isFinite(selectedIncidenceId) ? row.raw?.id === selectedIncidenceId : false

                      return (
                        <ListItemButton
                          key={row.key}
                          disableGutters
                          onClick={onSelect ? () => onSelect(row.raw) : undefined}
                          selected={isSelected}
                          sx={{
                            px: 1,
                            py: 1,
                            minHeight: itemMinHeight,
                            borderRadius: 2.5,
                            alignItems: 'flex-start',
                            gap: 1,
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.14)}`,
                            bgcolor: alpha(theme.palette.background.default, 0.14),
                            '&:hover': {
                              bgcolor: alpha(theme.palette.background.default, 0.22)
                            },
                            '&.Mui-selected': {
                              bgcolor: alpha(theme.palette.primary.main, 0.14),
                              borderColor: alpha(theme.palette.primary.main, 0.28)
                            },
                            '&.Mui-selected:hover': {
                              bgcolor: alpha(theme.palette.primary.main, 0.18)
                            }
                          }}
                          aria-label={t_('Incidence history item')}
                        >
                          <Box
                            sx={{
                              width: 4,
                              alignSelf: 'stretch',
                              borderRadius: 999,
                              bgcolor: row.color,
                              opacity: row.isOpen ? 1 : 0.8,
                              flex: '0 0 auto'
                            }}
                          />
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Stack direction='row' spacing={0.75} alignItems='center' sx={{ minWidth: 0 }}>
                              <Typography
                                variant='body2'
                                sx={{
                                  fontWeight: 700,
                                  lineHeight: 1.2,
                                  minWidth: 0,
                                  display: '-webkit-box',
                                  WebkitBoxOrient: 'vertical',
                                  WebkitLineClamp: compact ? 2 : (showMeta ? 2 : 1),
                                  overflow: 'hidden'
                                }}
                              >
                                {row.title}
                              </Typography>
                              {row.hasComment
                                ? (
                                  <Box
                                    sx={{
                                      width: 6,
                                      height: 6,
                                      borderRadius: '50%',
                                      bgcolor: alpha(theme.palette.info.main, 0.9),
                                      flex: '0 0 auto'
                                    }}
                                  />
                                  )
                                : null}
                            </Stack>
                            {compact
                              ? (
                                <Typography
                                  variant='caption'
                                  sx={{
                                    display: 'inline-flex',
                                    mt: 0.4,
                                    px: 0.75,
                                    py: 0.2,
                                    borderRadius: 999,
                                    bgcolor: alpha(row.color, row.isOpen ? 0.18 : 0.12),
                                    border: `1px solid ${alpha(row.color, 0.45)}`,
                                    lineHeight: 1.2,
                                    maxWidth: '100%',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  {row.statusLabel}
                                </Typography>
                                )
                              : (
                                <Stack direction='row' spacing={0.5} alignItems='center' sx={{ mt: 0.3 }}>
                                  <Chip
                                    size='small'
                                    label={row.statusLabel}
                                    sx={{
                                      height: 18,
                                      fontSize: 11,
                                      bgcolor: alpha(row.color, row.isOpen ? 0.18 : 0.12),
                                      color: 'text.primary',
                                      border: `1px solid ${alpha(row.color, 0.45)}`
                                    }}
                                  />
                                </Stack>
                                )}
                            {showMeta && meta
                              ? (
                                <Typography
                                  variant='caption'
                                  sx={{
                                    display: 'block',
                                    mt: 0.35,
                                    color: 'text.secondary',
                                    lineHeight: 1.2,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  {meta}
                                </Typography>
                                )
                              : null}
                          </Box>
                        </ListItemButton>
                      )
                    })}
                  </List>
                  )}
        </Box>
      </Stack>
    </Card>
  )
}

IncidenceHistoryColumn.propTypes = {
  incidences: PropTypes.array,
  loading: PropTypes.bool,
  error: PropTypes.any,
  onSelect: PropTypes.func,
  selectedIncidenceId: PropTypes.number,
  compact: PropTypes.bool,
  expanded: PropTypes.bool,
  onToggleExpanded: PropTypes.func,
  fullWidth: PropTypes.bool,
  headerAction: PropTypes.shape({
    icon: PropTypes.node,
    ariaLabel: PropTypes.string,
    onClick: PropTypes.func
  }),
  elevation: PropTypes.number,
  cardSx: PropTypes.object
}

export default IncidenceHistoryColumn
